import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Info } from 'lucide-react'
import { Typography } from '@/components/ui/typography'
import { formatCurrency, parseCurrency } from '@/lib/utils'
import dayjs from '@/lib/dayjs'
import type {
  PlatformEarningsSummary,
  ProcessingFeeCoverage,
} from '@/lib/services/platform-ledger/platform-ledger.types'

interface ProcessingCostCoverageProps {
  coverage?: ProcessingFeeCoverage | undefined
}

export function ProcessingCostCoverage({ coverage }: ProcessingCostCoverageProps) {
  if (!coverage || coverage.status === 'not_applicable') return null

  const dateLabel = (date: string) =>
    dayjs.tz(date, coverage.timezone || 'UTC').format('MMM D, YYYY')
  const timestampLabel = (date: string) =>
    dayjs(date)
      .tz(coverage.timezone || 'UTC')
      .format('MMM D, YYYY, h:mm A')
  const firstGap = coverage.gaps?.[0]

  return (
    <div className="space-y-1" role="status">
      <Typography as="p" variant="caption" tone="muted">
        Merchant Fees are excluded until their payment processing costs are matched. Matched fees
        and costs are reported on the original payment date.
      </Typography>
      {coverage.status === 'provisional' && firstGap && (
        <>
          <Typography as="p" variant="caption" tone="muted">
            Processing cost imports are incomplete. Platform Revenue may change as more payment
            processing or Radar costs arrive.{' '}
            {coverage.covered_through
              ? `Latest fully covered import date: ${dateLabel(coverage.covered_through)}.`
              : 'No fully covered import day yet.'}
          </Typography>
          <Typography as="p" variant="caption" tone="muted">
            {coverage.gaps.length} import {coverage.gaps.length === 1 ? 'gap' : 'gaps'}. Earliest
            missing interval: {timestampLabel(firstGap.start)} up to {timestampLabel(firstGap.end)}.
          </Typography>
        </>
      )}
      <Typography as="p" variant="caption" tone="muted">
        {coverage.merchant_fees_accurate_through
          ? `Payment processing costs matched through ${dateLabel(coverage.merchant_fees_accurate_through)}.`
          : 'No fully reconciled day yet.'}
        {coverage.earliest_unmatched_payment_date
          ? ` Earliest payment awaiting payment processing costs: ${dateLabel(coverage.earliest_unmatched_payment_date)}. ${coverage.unmatched_payment_count} payments awaiting costs.`
          : ''}
        {coverage.unverified_merchant_fee_count > 0
          ? ` ${coverage.unverified_merchant_fee_count} Merchant Fee records await historical payment ownership verification.`
          : ''}
        {coverage.pending_count > 0
          ? ` ${coverage.pending_count} matched processing fee groups (${formatCurrency(coverage.pending_amount)}) need classification review.`
          : ''}
      </Typography>
    </div>
  )
}

interface PlatformEarningsBreakdownProps {
  summary: PlatformEarningsSummary
  coverage?: ProcessingFeeCoverage | undefined
  showTotal?: boolean
  merchantOnly?: boolean
}

export function PlatformEarningsBreakdown({
  summary,
  coverage,
  showTotal = true,
  merchantOnly = false,
}: PlatformEarningsBreakdownProps) {
  if (!summary) return null

  const rows: [label: string, amount: string | number][] = []

  if (!merchantOnly) {
    rows.push(
      ['Website / Platform Fees', summary.platform_fee_revenue],
      ['POS Fees', summary.pos_fee_revenue]
    )
  }

  if (summary.has_v2_activity) {
    rows.push(
      [
        'Merchant Fee Revenue Included',
        summary.included_merchant_fee_revenue ?? summary.merchant_fee_revenue,
      ],
      [
        'Payment Processing Cost',
        -parseCurrency(summary.card_processing_cost ?? summary.processor_cost),
      ]
    )

    if (summary.has_radar_activity) {
      rows.push(['Radar Cost', -parseCurrency(summary.radar_cost)])
    }

    rows.push(['Merchant Fee Margin', summary.merchant_fee_margin])
  }

  if (showTotal) {
    rows.push(['Platform Revenue', summary.net_revenue])
  }

  return (
    <div className="space-y-3">
      <dl className="space-y-2">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-baseline justify-between gap-4">
            <Typography as="dt" variant="supporting">
              {label}
            </Typography>
            <Typography
              as="dd"
              variant="supporting"
              weight={label === 'Platform Revenue' ? 'semibold' : 'normal'}
              className="tabular-nums"
            >
              {formatCurrency(value ?? 0)}
            </Typography>
          </div>
        ))}
      </dl>
      <ProcessingCostCoverage coverage={coverage} />
    </div>
  )
}

interface PlatformEarningsDetailsProps {
  /** Without earnings, the popover shows only the processing cost coverage. */
  summary?: PlatformEarningsSummary | undefined
  coverage?: ProcessingFeeCoverage | undefined
  merchantOnly?: boolean
}

export function PlatformEarningsDetails({
  summary,
  coverage,
  merchantOnly = false,
}: PlatformEarningsDetailsProps) {
  if (!summary && (!coverage || coverage.status === 'not_applicable')) return null

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghostMuted"
          size="icon-3xs"
          aria-label="View earnings and processing cost coverage"
        >
          <Info className="size-3.5" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent size="wide" align="start">
        {summary ? (
          <PlatformEarningsBreakdown
            summary={summary}
            coverage={coverage}
            merchantOnly={merchantOnly}
            showTotal={!merchantOnly}
          />
        ) : (
          <ProcessingCostCoverage coverage={coverage} />
        )}
      </PopoverContent>
    </Popover>
  )
}
